/**
 * When the client fetches `GET /config`, and the bound that gives the operator (R9 §3.3, D328).
 *
 *  - **On launch, once, off the critical path.** The fetch is queued behind a zero-delay timer
 *    from `main.tsx` *after* `createRoot().render()`, and nothing awaits it — not a route, not a
 *    screen, not a command. A launch with no cache runs on defaults until it lands, then the
 *    surfaces that subscribe re-render.
 *  - **On resume, through `installResumeRefresh`, floored at 15 minutes** — the same primitive
 *    `storeUpdate.ts` floors at six hours and `shellUpdate.ts` at one minute; a third cadence is a
 *    third argument to one function, not a third timer. **With 0–5 s of uniform jitter**, because
 *    the resume edge is the one place a population moves together (a building waking its phones
 *    at 07:30 — the brief's §7.2); the launch fetch carries none, a launch being a human's own
 *    timing already.
 *  - **Never on a schedule while the app is foregrounded.** No `setInterval` anywhere in this
 *    directory (`infra/tests/client-config-client.test.ts` asserts it). A resident holding the
 *    app open for an hour keeps the document they launched with, and the next flip reaches them on
 *    the next resume. That is the cost of not running a timer, stated so the halt runbook can
 *    quote it.
 *
 * So the operator's pickup bound is: server cache (≤ 60 s) + this floor (≤ 15 min) + the resident
 * actually resuming the app.
 *
 * **The request carries no identity in either direction** (R9 §5 item 4) — no bearer, no session
 * binding, no build header, no query string, no cookie (`fetchTransport` sends
 * `credentials: 'omit'`). It goes through `nativeApiTransport(fetchTransport)`, never a bare
 * `fetch`, for the reason `telemetry/sink.ts` records: a bare `fetch` from `capacitor://` on iOS
 * fails CORS, and this fetch is new surface that must work in the container or it works nowhere
 * that matters. **It does not go through `SessionManager.request()`** because there is no session
 * to attach and the rail must run signed out; the one-authenticated-door rule is about where
 * credentials are attached, and none are attached here.
 *
 * **Every failure is silent** (R9 §6). A `503 config-invalid`, a `429`, a network error and
 * airplane mode are one case to the resident: the cached document or the defaults stand, and no
 * sink event is emitted — the sink's events are D306's, not a general log.
 */

import { ROUTES } from '@selogate/core'
import { nativeApiTransport } from '../auth/nativeHttp'
import { fetchTransport, type ApiTransport } from '../auth/transport'
import { installResumeRefresh, type ResumeRefreshOptions } from '../device-cache/resume'
import { acceptFetchedDocument } from './store'

/** R9 §3.3 (🔸 D328's three numbers, each a one-line change). */
export const CONFIG_RESUME_MIN_INTERVAL_MS = 15 * 60 * 1000
export const CONFIG_RESUME_JITTER_MS = 5_000

export interface ClientConfigRefreshOptions {
  /** Injected in tests; `nativeApiTransport(fetchTransport)` otherwise. */
  readonly transport?: ApiTransport
  readonly now?: () => number
  /** Uniform in `[0, 1)`; `Math.random` otherwise. */
  readonly random?: () => number
  readonly resume?: ResumeRefreshOptions
}

const defaultTransport: ApiTransport = nativeApiTransport(fetchTransport)

/**
 * Installs the launch fetch and the resume fetch. Returns the teardown. Idempotent per call —
 * two installs are two rails, so `main.tsx` calls it once.
 */
export function installClientConfigRefresh(options: ClientConfigRefreshOptions = {}): () => void {
  const {
    transport = defaultTransport,
    now = () => Date.now(),
    random = () => Math.random(),
    resume = {},
  } = options

  let installed = true
  let inFlight = false
  let pending: ReturnType<typeof setTimeout> | undefined

  function fetchOnce(): void {
    if (!installed || inFlight) return
    inFlight = true
    void transport({ method: ROUTES.clientConfig.method, path: ROUTES.clientConfig.template })
      .then((response) => {
        if (!installed || response.status !== 200) return
        acceptFetchedDocument(response.body, now())
      })
      .catch(() => {
        /* R9 §6 — indistinguishable from airplane mode, by design */
      })
      .finally(() => {
        inFlight = false
      })
  }

  function schedule(delayMs: number): void {
    clearTimeout(pending)
    pending = setTimeout(() => {
      pending = undefined
      fetchOnce()
    }, delayMs)
  }

  // Launch: off the critical path, no jitter.
  schedule(0)

  // Resume: floored, jittered. Returns `true` so the floor's clock is consumed — a declined
  // resume never happens here (the fetch runs signed out and offline alike; offline it fails
  // silently), so there is no reason to leave the window open.
  const teardownResume = installResumeRefresh(
    () => {
      if (!installed) return false
      schedule(Math.floor(random() * CONFIG_RESUME_JITTER_MS))
      return true
    },
    { minIntervalMs: CONFIG_RESUME_MIN_INTERVAL_MS, ...resume },
  )

  return () => {
    installed = false
    clearTimeout(pending)
    pending = undefined
    teardownResume()
  }
}
